import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, Observable, of, take } from 'rxjs';
import { Nullable } from 'primeng/ts-helpers';
import { MapDataAdapter } from '@app/main/main-content/map/services/map-data.adapter';
import { IAdapterMapConfig } from '@app/main/main-content/map/interfaces/adapter-map-config.interface';
import { IMapTarget } from '@app/main/main-content/map/interfaces/map-target.interface';
import { ILoggerMarker } from '@app/main/main-content/map/interfaces/logger-marker.interface';
import { IEventMarker } from '@app/main/main-content/map/interfaces/event-marker.interface';
import { IPipePolyline } from '@app/main/main-content/map/interfaces/pipe-polyline.interface';
import { Store } from '@ngrx/store';
import { selectPipesSource } from '@app/main/main-screen/store/data/pipes/pipes.selectors';
import { MapPipesService } from '@app/main/main-content/map/layers/pipes/merge/services/map-pipes.service';
import { IMapPipe } from '@app/main/main-content/map/layers/pipes/merge/interfaces/map-pipe.interface';
import { LeakDetectionMapService } from '@app/main/leak-detection/services/leak-detection-map/leak-detection-map.service';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { LdEventStatus } from '@app/main/leak-detection/enums/ld-event-status.enum';
import { MAP_POSITION_PICK_CURSOR } from '@app/main/main-content/map/configs/map-cursor.config';

const DEFAULT_PICKER_ZOOM = 16;

const NEW_EVENT_MARKER_ID = Math.random().toString(36).slice(2);

@Injectable()
export class DialogEventLocationMapService extends MapDataAdapter {
  private readonly leakDetectionMap = inject(LeakDetectionMapService);
  private readonly dialogState = inject(DialogEventState);
  private readonly store = inject(Store);
  private readonly mapPipesService = inject(MapPipesService);

  public readonly pinPosition = signal(this.dialogState.draft().coordinates);

  private readonly eventMarkers$ = toObservable(
    computed((): IEventMarker[] => {
      const event = this.dialogState.event();
      const position = this.pinPosition();

      if (!position) return [];

      return [
        {
          id: event?.id ?? NEW_EVENT_MARKER_ID,
          type: event?.status ?? LdEventStatus.New,
          position,
          raw: event,
          config: { showTooltip: false },
        },
      ];
    })
  );

  constructor() {
    super();

    this.mapCursorTrigger(MAP_POSITION_PICK_CURSOR);
  }

  public override getMapConfig(): IAdapterMapConfig {
    return {
      ...this.leakDetectionMap.getMapConfig(),
      mapTypeControl: false,
      mapCameraControl: false,
      mapStreetViewControl: false,
      mapScaleControl: false,
      mapStandaloneZoomControl: true,
      loggerMarkerOptimization: false,
      correlationMarkerOptimization: false,
      correlationPathOptimization: false,
      eventMarkerOptimization: false,
      toolbarNativeSearch: false,
      streetViewMarkerOverlay: false,
    };
  }

  public override getMapTarget(): Observable<Nullable<IMapTarget>> {
    const center = this.pinPosition();

    return center ? of({ center, zoom: DEFAULT_PICKER_ZOOM }) : this.leakDetectionMap.mapTargetChanged$.pipe(take(1));
  }

  public override getLoggerMarkers(): Observable<ILoggerMarker[]> {
    return this.leakDetectionMap
      .getLoggerMarkers()
      .pipe(map(markers => markers.map(marker => ({ ...marker, config: { ...marker.config, showTooltip: false } }))));
  }

  public override getEventMarkers(): Observable<IEventMarker[]> {
    return this.eventMarkers$;
  }

  public override getPipes(): Observable<IPipePolyline[]> {
    return this.mapPipesService
      .getPipes$(this.store.select(selectPipesSource))
      .pipe(map(pipes => this.toPipePolylines(pipes)));
  }

  private readonly toPipePolylines = this.mapPipesService.createPolylineMapper(
    (pipe): IPipePolyline<IMapPipe> => ({
      ...this.mapPipesService.toPolylineBase(pipe),
      config: { clickable: false, showTooltip: false },
    })
  );
}
